import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useFavoritesCount } from "@/contexts/FavoritesCountContext";
import { useLang } from "@/contexts/LangContext";
import api from "@/lib/api";

/**
 * Localised noun tables used to build the success toast after applying a
 * highlight. Kept here (rather than in i18n JSON) because the message has
 * to agree in gender with the noun, which the surface-aware hook is the
 * natural owner of.
 */
const NOUNS = {
    bible: {
        es: { sing: "versículo", plur: "versículos", adj: "resaltado" },
        en: { sing: "verse",     plur: "verses" },
    },
    catechism: {
        es: { sing: "párrafo",   plur: "párrafos",   adj: "resaltado" },
        en: { sing: "paragraph", plur: "paragraphs" },
    },
    prayers: {
        es: { sing: "oración",   plur: "oraciones",  adj: "resaltada" },
        en: { sing: "prayer",    plur: "prayers" },
    },
};

/**
 * Shared logic that powers the colour-highlight system across Bible,
 * Catechism and Prayers. It owns:
 *   • the `saved` Map (`key → { favId, color }`) of the user's saved rows
 *     in the current surface,
 *   • the asynchronous `applyHighlight(keys, colorId)` writer that PATCHes
 *     an existing favourite's colour or POSTs a fresh one,
 *   • `clearHighlight(keys)` which DELETEs every favourite under the
 *     supplied keys,
 *   • `getRowColor(key)` for the SelectionActionBar's "is-already-X" check.
 *
 * The hook is intentionally agnostic about how each surface composes its
 * keys or builds its POST payload, so the page-level component supplies:
 *   • `filterFavorite(fav)` — optional fine-grained filter (e.g. Bible
 *     restricts to `metadata.kind === "verse"` rows of the active `lang`).
 *   • `keyFromFavorite(fav)` — translates a favourite document into the
 *     local Map key (string). Return null/undefined to skip.
 *   • `buildPayload(key)` — async; returns `{ title, content, metadata,
 *     source_url? }` used to POST a new favourite, or null to abort.
 *   • `extraDeps` — extra reactive deps for the load effect (e.g. `lang`
 *     and `entries` arrays).
 */
export function useHighlightedFavourites({
    section,
    filterFavorite,
    keyFromFavorite,
    buildPayload,
    extraDeps = [],
}) {
    const { user } = useAuth();
    const { lang } = useLang();
    const { refresh: refreshFavCount } = useFavoritesCount();
    const navigate = useNavigate();

    const [saved, setSaved] = React.useState(() => new Map());

    React.useEffect(() => {
        if (!user) { setSaved(new Map()); return undefined; }
        let cancelled = false;
        (async () => {
            try {
                const r = await api.get("/favorites");
                if (cancelled) return;
                const map = new Map();
                for (const f of r.data || []) {
                    if (f.section !== section) continue;
                    if (filterFavorite && !filterFavorite(f)) continue;
                    const k = keyFromFavorite(f);
                    if (k == null) continue;
                    map.set(String(k), { favId: f.id, color: f.color_id || null });
                }
                setSaved(map);
            } catch { /* silent — no indicators if unauth/offline */ }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, section, lang, ...extraDeps]);

    const applyHighlight = React.useCallback(async (keys, colorId) => {
        if (!user) { navigate("/login"); return ""; }
        let created = 0;
        let recoloured = 0;
        for (const rawKey of keys) {
            const key = String(rawKey);
            const existing = saved.get(key);
            try {
                if (existing) {
                    await api.patch(`/favorites/${existing.favId}`, { color_id: colorId });
                    setSaved((m) => {
                        const n = new Map(m);
                        n.set(key, { favId: existing.favId, color: colorId });
                        return n;
                    });
                    recoloured += 1;
                } else {
                    const payload = await buildPayload(key);
                    if (!payload) continue;
                    const r = await api.post("/favorites", {
                        section,
                        color_id: colorId,
                        lang,
                        ...payload,
                    });
                    const newId = r.data?.id;
                    if (newId) {
                        setSaved((m) => {
                            const n = new Map(m);
                            n.set(key, { favId: newId, color: colorId });
                            return n;
                        });
                        created += 1;
                    }
                }
            } catch { /* best-effort batch */ }
        }
        refreshFavCount();
        const total = created + recoloured;
        if (total === 0) return "";
        const noun = (NOUNS[section] || NOUNS.bible)[lang] || NOUNS[section].en;
        if (lang === "es") {
            const adj = total === 1 ? noun.adj : `${noun.adj}s`;
            return `${total} ${total === 1 ? noun.sing : noun.plur} ${adj}`;
        }
        return `${total} ${total === 1 ? noun.sing : noun.plur} highlighted`;
    }, [user, saved, lang, navigate, refreshFavCount, section, buildPayload]);

    const clearHighlight = React.useCallback(async (keys) => {
        if (!user) return "";
        let removed = 0;
        for (const rawKey of keys) {
            const key = String(rawKey);
            const existing = saved.get(key);
            if (!existing) continue;
            try {
                await api.delete(`/favorites/${existing.favId}`);
                setSaved((m) => { const n = new Map(m); n.delete(key); return n; });
                removed += 1;
            } catch { /* best-effort */ }
        }
        refreshFavCount();
        if (removed === 0) return "";
        if (lang === "es") {
            return `${removed} resaltado${removed === 1 ? "" : "s"} eliminado${removed === 1 ? "" : "s"}`;
        }
        return `${removed} highlight${removed === 1 ? "" : "s"} removed`;
    }, [user, saved, lang, refreshFavCount]);

    const getRowColor = React.useCallback(
        (key) => saved.get(String(key))?.color || null,
        [saved],
    );

    return { saved, applyHighlight, clearHighlight, getRowColor };
}
